'use client';

import { ReviewsDataDocument } from '@packages/gql/generated/shopping/ReviewsDataDocument';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useParams, usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';
import { useIntl } from 'react-intl';
import { LinearProgress } from '@packages/shared/src/ui/LinearProgress/LinearProgress';
import { Button } from '@packages/shared/src/ui/Button/Button';
import type { CustomerRatingsParams } from '../types';
import { CustomerReviewCard } from '../../CustomerReviews/CustomerReviewCard/CustomerReviewCard.client';
import { ratingFilterTagPrefix } from '../../CustomerRatings/config';

/* GraphQL */ `
  query ReviewsData(
    $productId: String!
    $locale: String!
    $start: Int!
    $count: Int!
    $filter: String
  ) {
    rating(id: $productId, locale: $locale, start: $start, count: $count, filter: $filter) {
      product {
        reviewCount
        reviews {
          id
          ...CustomerReviewCardFragment
        }
      }
    }
  }
`;

/**
 * Paginated list of ratings with load more button
 */
export const CustomerReviewsListWithPaging = () => {
  const { formatMessage } = useIntl();
  const [cursor, setCursor] = useState(0);
  const {
    customerRatings: { pagingCount },
  } = useConfig();

  const pathname = usePathname();
  const params = useParams<CustomerRatingsParams>();
  const { locale } = useI18n();

  const ftag = params?.ftag ? decodeURIComponent(params.ftag[0]) : undefined;

  // reset cursor after applying, changing or removing filter, so the reviews are shown from 0 again
  // e.g. 20 reviews were already laoded. Now you click on a filter, therefore a new request is sent to load the reviews for that filter.
  // We dont want to load the reviews from 20 tough, so we have to reset cursor to 0.
  // TODO: We could use a atom for the cursor state and update it on filter click outside of RatingList, so we don't need pathname and the useState
  // I guess the concept is that the review detail page can be accessed with a filter -> thats why it is a param
  useEffect(() => {
    setCursor(0);
  }, [pathname]);

  // file "reviewsPagination.ts" takes care about the stacking of reviews
  const [{ data }] = useQuery({
    query: ReviewsDataDocument,
    variables: {
      productId: (params?.akl ?? params?.slug) as string,
      locale,
      start: cursor,
      count: pagingCount,
      filter: ftag ? ftag.replace(ratingFilterTagPrefix, '') : undefined,
    },
    context: useMemo(() => ({ suspense: false }), []),
  });

  const product = data?.rating?.product;

  if (!product) {
    return null;
  }

  const { reviews, reviewCount } = product;

  return (
    <div>
      <div className="flex flex-col gap-2">
        {reviews.map((review) => (
          <CustomerReviewCard data={review} key={review.id} />
        ))}
      </div>
      {/* Load More area */}
      <div className="mx-auto mt-2 flex max-w-[300px] flex-col gap-1.5">
        <p className="text-size-body2 text-center">
          {formatMessage(
            {
              id: 'customer-ratings.paging.count.label',
              defaultMessage: 'Du hast {shown} von {reviewCount} Bewertungen gesehen',
            },
            { shown: reviews.length, reviewCount },
          )}
        </p>
        <LinearProgress
          mode="determinateWithoutValue"
          value={(reviews.length * 100) / reviewCount}
        />
        <Button onClick={() => setCursor(reviews.length)} disabled={reviews.length === reviewCount}>
          {formatMessage({
            id: 'customer-ratings.paging.load-more',
            defaultMessage: 'Mehr Bewertungen anzeigen',
          })}
          <span className="icon-emp-load-reload" />
        </Button>
      </div>
    </div>
  );
};
